import React, { useMemo, useState } from 'react';
import {
  Activity, AlertTriangle, Check, Database, FileClock, Filter, LockKeyhole, Search,
  ShieldCheck, UserRound,
} from 'lucide-react';
import { Badge, EmptyState, PageHeader, SectionHeader } from '../components/ui.jsx';
import { formatDate } from '../utils.js';

export default function AuditPage({ state }) {
  const [search, setSearch] = useState('');
  const [errorSearch, setErrorSearch] = useState('');
  const audits = state?.audits || [];
  const aiErrors = state?.aiErrorLogs || [];
  const visible = useMemo(() => audits.filter((item) => !search || `${item.actor}${item.action}${item.entityType}`.toLocaleLowerCase().includes(search.toLocaleLowerCase())), [audits, search]);
  const visibleErrors = useMemo(() => aiErrors.filter((item) => !errorSearch || [
    item.candidateName, item.jobTitle, item.providerName, item.modelId, item.errorCode,
    item.errorMessage, item.purpose, item.role,
  ].join(' ').toLocaleLowerCase().includes(errorSearch.toLocaleLowerCase())), [aiErrors, errorSearch]);
  return (
    <div className="page audit-page">
      <PageHeader eyebrow="治理与合规" title="审计记录" description="模型设置、岗位变更、评分版本与人工判断均可追溯" />
      <section className="audit-policy-grid">
        <article><span><ShieldCheck size={20} /></span><div><strong>不自动拒绝</strong><p>AI 只排序并建议人工动作</p></div><Badge tone="success"><Check size={12} />启用</Badge></article>
        <article><span><LockKeyhole size={20} /></span><div><strong>受保护信号隔离</strong><p>{state?.system?.protectedSignals?.length || 0} 类信号明确禁用</p></div><Badge tone="success"><Check size={12} />启用</Badge></article>
        <article><span><Database size={20} /></span><div><strong>评分版本保留</strong><p>AI 重评与人工校准分开存储</p></div><Badge tone="success"><Check size={12} />启用</Badge></article>
      </section>
      <section className="panel ai-error-log-panel">
        <SectionHeader title="AI 错误日志" description="逐次记录 Provider 请求、Fallback、耗时和上游诊断信息" action={<label className="search-field"><Search size={15} /><input value={errorSearch} onChange={(event) => setErrorSearch(event.target.value)} placeholder="搜索模型、候选人或错误码" /></label>} />
        {visibleErrors.length === 0 ? <EmptyState icon={Check} title="没有 AI 错误记录" description="后续模型请求若失败，会在这里保留逐次诊断信息。" /> : (
          <div className="ai-error-log-list">{visibleErrors.map((item) => {
            const hasDetail = item.errorDetail && Object.keys(item.errorDetail).length > 0;
            return (
              <article key={item.id}>
                <span className="ai-error-icon"><AlertTriangle size={17} /></span>
                <div className="ai-error-main">
                  <div><strong>{item.providerName || '未解析 Provider'} / {item.modelId || '未选择模型'}</strong><Badge tone="danger">{item.errorCode || 'model_error'}</Badge>{item.role === 'fallback' && <Badge tone="warning">Fallback</Badge>}</div>
                  <p>{item.errorMessage || '模型请求失败，未返回错误说明。'}</p>
                  <small>{item.candidateName || '非候选人任务'}{item.jobTitle ? ` · ${item.jobTitle}` : ''} · {item.purpose} {`· 第 ${item.attempt}/${item.maxAttempts} 次`}{item.elapsedMs ? ` · ${(item.elapsedMs / 1000).toFixed(1)} 秒` : ''}</small>
                  {hasDetail && <details><summary>查看诊断详情</summary><pre>{JSON.stringify(item.errorDetail, null, 2)}</pre></details>}
                </div>
                <time>{formatDate(item.createdAt, true)}</time>
              </article>
            );
          })}</div>
        )}
      </section>
      <section className="panel audit-log-panel">
        <SectionHeader title="最近活动" description="当前工作区的重要数据变更" action={<label className="search-field"><Search size={15} /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="搜索记录" /></label>} />
        {visible.length === 0 ? <EmptyState icon={Filter} title="没有匹配的审计记录" /> : (
          <div className="audit-list">{visible.map((item) => (
            <article key={item.id}><span className="audit-icon"><Activity size={17} /></span><div><strong>{item.action}</strong><p><UserRound size={13} />{item.actor}<span />{item.entityType}{item.entityId ? ` · ${item.entityId}` : ''}</p></div><time>{formatDate(item.createdAt, true)}</time><Badge>{Object.keys(item.detail || {}).length} 个字段</Badge></article>
          ))}</div>
        )}
      </section>
      <section className="audit-retention-band"><FileClock size={20} /><div><strong>数据保留策略</strong><p>生产部署时建议将审计日志写入独立 Supabase schema，并由数据库角色禁止业务账号修改历史记录。</p></div></section>
    </div>
  );
}
